import { useCallback, useEffect, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { SearchBox } from "../../../components/Search";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { formatDateForAPI } from "../Utility/const";
import {
  getRajasthanGssRestrictedAreaSummaryMetrics,
  getRajasthanGssRestrictedSummaryMetricsDiscom,
  getRajasthanGssRestrictedSummaryMetricsTotal,
} from "../Utility/RestApi";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const GROUP_BY = [
  {
    label: "Area",
    key: "area",
    values: ["area", "discom"],
  },
  {
    label: "Total",
    key: "total",
    values: ["total"],
  },
  {
    label: "Discom",
    key: "discom",
    values: ["discom"],
  },
];
const DISCOM = [
  { label: "Jodhpur", key: "Jodhpur", apiKey: "JDVVNL" },
  { label: "Jaipur", key: "Jaipur", apiKey: "JVVNL" },
  { label: "Ajmer", key: "Ajmer", apiKey: "AVVNL" },
];

// Helper function to convert display names to API keys
function getApiDiscomKeys(displayKeys: string[]): string[] {
  return displayKeys.map((displayKey) => {
    const discom = DISCOM.find((d) => d.key === displayKey);
    return discom ? discom.apiKey : displayKey;
  });
}

// Helper function to convert API keys to display names
function getDisplayDiscomName(apiKey: string): string {
  const discom = DISCOM.find((d) => d.apiKey === apiKey);
  return discom ? discom.label : apiKey;
}

interface RajasthanDrawalSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: any[];
  onGroupByChange: (index: number) => void;
}

export function RajasthanDrawalSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
}: RajasthanDrawalSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [loading, setLoading] = useState(true);

  const [availableAreas, setAvailableAreas] = useState<string[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  const fetchSummaryData = useCallback(async () => {
    if (!DefaultDates || !DefaultDates[activeTab]) {
      return;
    }

    const trendDates = DefaultDates[activeTab]?.defaultTrendDates || [];
    if (trendDates.length === 0) {
      console.warn("No trend dates available");
      setSummaryData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Get first and last date from all trend date ranges
      const allDates = trendDates.flatMap((dateRange: any) => {
        const [start, end] = dateRange.value.split(",");
        return [start, end].filter(Boolean);
      });

      if (allDates.length === 0) {
        console.error("No valid dates found");
        setSummaryData([]);
        setLoading(false);
        return;
      }

      const sortedDates = allDates.sort(
        (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime()
      );
      const startDateStr = sortedDates[0];
      const endDateStr = sortedDates[sortedDates.length - 1];

      console.log("Fetching summary data with dates:", { startDateStr, endDateStr });

      let data: any[] = [];
      const isAreaGrouping = selectedGroupKeys[0]?.key === "area";
      const isDiscomGrouping = selectedGroupKeys[0]?.key === "discom";
      const isTotalGrouping = selectedGroupKeys[0]?.key === "total";

      if (isAreaGrouping) {
        data = await getRajasthanGssRestrictedAreaSummaryMetrics(startDateStr, endDateStr);
      } else if (isDiscomGrouping) {
        data = await getRajasthanGssRestrictedSummaryMetricsDiscom(startDateStr, endDateStr);
      } else if (isTotalGrouping) {
        data = await getRajasthanGssRestrictedSummaryMetricsTotal(startDateStr, endDateStr);
      } else {
        data = await getRajasthanGssRestrictedAreaSummaryMetrics(startDateStr, endDateStr);
      }

      setSummaryData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedGroupKeys]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  useEffect(() => {
    console.log("Processing data, summaryData length:", summaryData.length);
    console.log("Selected discom filter:", selectedDiscom);
    console.log("Selected group:", selectedGroupKeys[0]?.key);

    if (summaryData.length === 0) {
      console.warn("No summary data to process");
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    let filteredData = summaryData;

    // Filter by discom (only for Area and Discom grouping, not for Total)
    if (selectedGroupKeys[0]?.key !== "total" && selectedDiscom.length > 0) {
      const apiDiscomKeys = getApiDiscomKeys(selectedDiscom);
      console.log("Filtering by discom API keys:", apiDiscomKeys);
      filteredData = filteredData.filter((item) =>
        apiDiscomKeys.includes(item.discom)
      );
      console.log("After discom filter:", filteredData.length, "items");
    }

    // Filter by selected blocks
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((opt) => opt.value).includes(item.slot)
      );
    }

    // Build available areas for Area grouping
    if (selectedGroupKeys[0]?.key === "area") {
      const areas = Array.from(
        new Set(
          filteredData
            .map((item: any) => {
              const stationName = item.station || "UNKNOWN";
              return `${stationName} (${getDisplayDiscomName(item.discom)})`;
            })
            .filter((name) => !name.startsWith("UNKNOWN"))
        )
      ).sort();
      setAvailableAreas(areas);
    }

    if (filteredData.length === 0) {
      console.error("No data available after filtering");
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    // Process data based on group by selection
    if (selectedGroupKeys[0]?.key === "area") {
      if (selectedAreas.length > 0) {
        filteredData = filteredData.filter((item) => {
          const stationName = item.station || "UNKNOWN";
          const areaKey = `${stationName} (${getDisplayDiscomName(item.discom)})`;
          return selectedAreas.map((opt: any) => opt.value).includes(areaKey);
        });
      }

      const slotMap = new Map<number, any>();

      filteredData.forEach((item) => {
        const slot = item.slot;
        const station = item.station || "UNKNOWN";
        const discomApi = item.discom;
        const stationKey = `${station}_${discomApi}`;

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot: slot,
            slot_number: slot,
            slot_with_time: item.time_string
              ? `${slot} (${item.time_string})`
              : slot,
          });
        }

        const slotData = slotMap.get(slot);

        slotData[`${stationKey}_min_load`] = item.min_load * conversionFactor;
        slotData[`${stationKey}_max_load`] = item.max_load * conversionFactor;
        slotData[`${stationKey}_avg_load`] = item.avg_load * conversionFactor;
        slotData[`${stationKey}_mode_load`] = item.mode_load * conversionFactor;
        slotData[`${stationKey}_date_of_min`] = item.date_of_min_load;
        slotData[`${stationKey}_date_of_max`] = item.date_of_max_load;
      });

      const processedData = Array.from(slotMap.values());
      processedData.sort((a, b) => a.slot - b.slot);

      setChartData(processedData);
      setTableData(processedData);

      const uniqueStations = Array.from(
        new Set(
          filteredData.map((item) => {
            const station = item.station || "UNKNOWN";
            return `${station}_${item.discom}`;
          })
        )
      ).sort();
      setChartKeys(uniqueStations);
    } else if (selectedGroupKeys[0]?.key === "discom") {
      const slotMap = new Map<number, any>();

      filteredData.forEach((item) => {
        const slot = item.slot;
        const discomApi = item.discom;

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot: slot,
            slot_number: slot,
            slot_with_time: item.time_string
              ? `${slot} (${item.time_string})`
              : slot,
          });
        }

        const slotData = slotMap.get(slot);

        slotData[`${discomApi}_min_load`] = item.min_load * conversionFactor;
        slotData[`${discomApi}_max_load`] = item.max_load * conversionFactor;
        slotData[`${discomApi}_avg_load`] = item.avg_load * conversionFactor;
        slotData[`${discomApi}_mode_load`] = item.mode_load * conversionFactor;
        slotData[`${discomApi}_date_of_min`] = item.date_of_min_load;
        slotData[`${discomApi}_date_of_max`] = item.date_of_max_load;
      });

      const processedData = Array.from(slotMap.values());
      processedData.sort((a, b) => a.slot - b.slot);

      setChartData(processedData);
      setTableData(processedData);

      const uniqueDiscoms = Array.from(
        new Set(filteredData.map((item) => item.discom))
      ).sort();

      setChartKeys(uniqueDiscoms);
    } else if (selectedGroupKeys[0]?.key === "total") {
      // For Total grouping, use data directly from the Total API
      const slotMap = new Map<number, any>();

      filteredData.forEach((item) => {
        const slot = item.slot;

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot: slot,
            slot_number: slot,
            slot_with_time: item.time_string
              ? `${slot} (${item.time_string})`
              : slot,
            total_min_load: item.min_load * conversionFactor,
            total_max_load: item.max_load * conversionFactor,
            total_avg_load: item.avg_load * conversionFactor,
            total_mode_load: item.mode_load * conversionFactor,
            total_date_of_min: item.date_of_min_load,
            total_date_of_max: item.date_of_max_load,
          });
        }
      });

      const processedData = Array.from(slotMap.values());
      processedData.sort((a, b) => a.slot - b.slot);

      setChartData(processedData);
      setTableData(processedData);
      setChartKeys(["total"]);
    }
  }, [
    summaryData,
    selectedUnit,
    selectedDiscom,
    selectedGroupKeys,
    selectedAreas,
    selectedBlock,
  ]);

  const getLegends = () => {
    const legends: any[] = [];
    const metricTypes = [
      { field: "min", displayName: "Min", hasDate: true },
      { field: "max", displayName: "Max", hasDate: true },
      { field: "avg", displayName: "Average", hasDate: false },
      { field: "mode", displayName: "Mode", hasDate: false },
    ];

    const validKeys = (chartKeys || []).filter(
      (key) => key && typeof key === "string" && key.trim() !== ""
    );

    validKeys.forEach((key) => {
      metricTypes.forEach((metric) => {
        const dataKey = `${key}_${metric.field}_load`;

        let baseName = "";

        try {
          if (selectedGroupKeys[0]?.key === "total") {
            baseName = "Total";
          } else if (selectedGroupKeys[0]?.key === "discom") {
            baseName = getDisplayDiscomName(key);
          } else if (selectedGroupKeys[0]?.key === "area") {
            if (key.includes("_")) {
              const parts = key.split("_");
              const discomKey = parts[parts.length - 1];
              const stationKey = parts.slice(0, -1).join("_");
              const discomDisplayName = getDisplayDiscomName(discomKey);
              baseName = `${stationKey} (${discomDisplayName})`;
            } else {
              baseName = key;
            }
          } else {
            baseName = key;
          }
        } catch (error) {
          console.error("Error creating legend name:", error, key);
          baseName = key;
        }

        const cleanKey = key.replace(/ /g, "_");
        const legendName = `${baseName} ${metric.displayName}`;
        const dateKey = metric.hasDate
          ? `${cleanKey}_date_of_${metric.field}`
          : undefined;

        legends.push({
          dataKey: dataKey,
          name: legendName,
          legendColor: undefined,
          stroke: undefined,
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
          dateKey: dateKey,
        });
      });
    });

    return legends;
  };

  const customTooltipFormatter = useCallback(
    (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      let items = Array.isArray(params) ? params : [params];

      let title = param.name;
      const isTimeSlot = true;
      if (isTimeSlot) {
        title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      }

      const dataPoint = chartData[param.dataIndex];

      const sortedItems = [...items].sort((a, b) => {
        const nameA = (a.seriesName || "").toLowerCase();
        const nameB = (b.seriesName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const tooltipItems = sortedItems.map((item) => {
        const value = parseFloat(item.value)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const bgColor = item.color;

        let displaySeriesName = item.seriesName;

        const seriesNameParts = item.seriesName.split(" ");
        const key = seriesNameParts.slice(0, -1).join("_").replace(/ /g, "_");
        const metricType =
          seriesNameParts[seriesNameParts.length - 1].toLowerCase();

        if (metricType === "min" && dataPoint?.[`${key}_date_of_min`]) {
          displaySeriesName = `${item.seriesName} (${
            dataPoint[`${key}_date_of_min`]
          })`;
        } else if (metricType === "max" && dataPoint?.[`${key}_date_of_max`]) {
          displaySeriesName = `${item.seriesName} (${
            dataPoint[`${key}_date_of_max`]
          })`;
        }

        return `
        <div class="tooltip-item" data-name="${item.seriesName?.toLowerCase()}">
          <div class="color-indicator" style="background-color: ${bgColor}"></div>
          <div class="series-name">${displaySeriesName}</div>
          <div class="value">${value} ${selectedUnit}</div>
        </div>
      `;
      });

      return `
      <div class="enhanced-tooltip">
        <div class="tooltip-header">
          <div class="tooltip-title">${title}</div>
        </div>
        
        <div class="tooltip-content">
          ${tooltipItems.join("")}
        </div>
      </div>
      
      <style>
        .enhanced-tooltip {
          font-family: 'Inter', system-ui, sans-serif;
          width: 100%;
          min-width: 200px;
        }
        .tooltip-header {
          display: flex;
          flex-direction: column;
          gap: 8px;
          justify-content: start;
          align-items: start;
          border-bottom: 1px solid #eaeaea;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .tooltip-title {
          font-weight: 600;
          font-size: 13px;
          color: #333;
        }
        .tooltip-content {
          max-height: 300px;
          overflow-y: auto;
          padding-right: 10px;
        }
        .tooltip-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        .tooltip-item:last-child {
          border-bottom: none;
        }
        .color-indicator {
          width: 12px;
          height: 12px;
          flex-shrink: 0;
        }
        .series-name {
          font-weight: 500;
          font-size: 12px;
          color: #333;
          flex: 1;
        }
        .value {
          font-weight: 600;
          font-size: 12px;
          color: #333;
        }
      </style>
    `;
    },
    [chartData, selectedUnit]
  );

  const getExtraKeys = () => {
    return ["slot_number", "Date"];
  };

  const getExtraLabels = () => {
    return ["Block", "Date"];
  };

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: getExtraKeys(),
    extraLabels: getExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand Summary",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}

      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              onUnitChange(item);
            }}
          />

          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={(item) => {
              onDiscomChange(item);
            }}
          />

          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            onSelect={(item, index) => {
              onGroupByChange(index);
              if (GROUP_BY[index].key !== "area") {
                setSelectedAreas([]);
              }
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-80">
              Restricted Demand Summary ({selectedUnit})
            </h2>
            <div className="flex gap-x-6 justify-end">
              {selectedGroupKeys[0]?.key === "area" && (
                <div className="min-w-40">
                  <SearchBox
                    options={availableAreas.map((item) => ({
                      value: item,
                      label: item,
                    }))}
                    isMany={true}
                    placeholder="Search Area"
                    selectedOptions={selectedAreas}
                    onChange={(v: any) => {
                      setSelectedAreas(v);
                    }}
                  />
                </div>
              )}
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {chartData.length === 0 && !loading && (
            <div
              style={{ padding: "40px", textAlign: "center", color: "#666" }}
            >
              No data available for the selected Dates.
            </div>
          )}

          {dataTab.label === "Chart" && chartData.length > 0 && (
            <ReMixChart
              key={`${selectedChartType.value}-${selectedUnit}`}
              data={chartData}
              showBrush={undefined}
              isTimeSlot={false}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={getLegends()}
              xDataKey="slot"
              secondXDataKey={undefined}
              customTooltip={customTooltipFormatter}
            />
          )}

          {dataTab.label === "Summary" && chartData.length > 0 && (
            <LegendSummaryTable {...tablePar} />
          )}
          {dataTab.label === "Table" && chartData.length > 0 && (
            <ChartTable {...tablePar} />
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>GSS Report (RUVITL)</p>}
              text="1.	Sum of all GSS load = Actual drawal (Restricted demand) 
2.	MW = Represents the average (MW) when the selected time interval is Date or Month."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}